import React, { useState, useEffect } from 'react'
import { useQuery, useMutation, gql } from '@apollo/client'
import { getVendor, editVendor, vendorResetPassword } from '../apollo'
import {
  Box, Typography, Paper, Grid, TextField, Button,
  Alert, CircularProgress, Avatar, Divider, IconButton, InputAdornment
} from '@mui/material'
import { Container } from '@mui/system'
import PersonIcon from '@mui/icons-material/Person'
import EmailIcon from '@mui/icons-material/Email'
import PhoneIcon from '@mui/icons-material/Phone'
import LockIcon from '@mui/icons-material/Lock'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import { replaceSessionToken } from '../utils/authToken'

const GET_VENDOR = gql`${getVendor}`
const EDIT_VENDOR = gql`${editVendor}`
const VENDOR_RESET_PASSWORD = gql`${vendorResetPassword}`

const sx = {
  input: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px', fontSize: '0.88rem',
      '& fieldset': { borderColor: '#e2e8f0' },
      '&:hover fieldset': { borderColor: '#818cf8' },
      '&.Mui-focused fieldset': { borderColor: '#818cf8' }
    }
  },
  btn: {
    borderRadius: '10px', textTransform: 'none', fontWeight: 600,
    px: 4, bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' }, boxShadow: 'none'
  }
}

const FieldLabel = ({ children }) => (
  <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#475569', mb: 0.75 }}>
    {children}
  </Typography>
)

const SectionCard = ({ title, subtitle, icon, children }) => (
  <Paper elevation={0} sx={{ borderRadius: '20px', border: '1px solid #e2e8f0', p: 3, mb: 3, boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
      <Box sx={{ width: 42, height: 42, borderRadius: '12px', background: 'linear-gradient(135deg, #818cf8, #6366f1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        {icon}
      </Box>
      <Box>
        <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#1e293b', lineHeight: 1.2 }}>{title}</Typography>
        <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8' }}>{subtitle}</Typography>
      </Box>
    </Box>
    <Divider sx={{ mb: 2.5, borderColor: '#f1f5f9' }} />
    {children}
  </Paper>
)

const VendorAccountProfile = () => {
  const vendorId = localStorage.getItem('vendorId')

  // Profile fields
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [success, setSuccess] = useState('')
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  // Password fields
  const [oldPassword, setOldPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showOld, setShowOld] = useState(false)
  const [showNew, setShowNew] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [pwSuccess, setPwSuccess] = useState('')
  const [pwError, setPwError] = useState('')
  const [savingPw, setSavingPw] = useState(false)

  const { data, loading } = useQuery(GET_VENDOR, {
    variables: { id: vendorId },
    skip: !vendorId
  })

  useEffect(() => {
    if (!data?.getVendor) return
    setName(data.getVendor.name || '')
    setPhone(data.getVendor.phone || '')
  }, [data?.getVendor?._id])

  const [mutate] = useMutation(EDIT_VENDOR)
  const [mutatePassword] = useMutation(VENDOR_RESET_PASSWORD)

  const handleSave = async e => {
    e.preventDefault()
    if (!name.trim()) { setError('Name is required'); return }
    setSaving(true)
    setError('')
    setSuccess('')
    try {
      await mutate({
        variables: {
          vendorInput: {
            _id: vendorId,
            name: name.trim(),
            phone: phone.trim(),
            email: data?.getVendor?.email || ''
          }
        }
      })
      setSuccess('Profile updated successfully')
      setTimeout(() => setSuccess(''), 4000)
    } catch (err) {
      setError(err.graphQLErrors?.[0]?.message || 'Failed to save changes')
      setTimeout(() => setError(''), 4000)
    } finally {
      setSaving(false)
    }
  }

  const handleChangePassword = async e => {
    e.preventDefault()
    setPwError('')
    setPwSuccess('')
    if (!oldPassword) { setPwError('Enter your current password'); return }
    if (newPassword.length < 6) { setPwError('New password must be at least 6 characters'); return }
    if (newPassword !== confirmPassword) { setPwError('Passwords do not match'); return }
    setSavingPw(true)
    try {
      const res = await mutatePassword({ variables: { oldPassword, newPassword } })
      // A new password signs out every other device; this one keeps going
      // with the fresh token (backend migration 137).
      replaceSessionToken(res?.data?.vendorResetPassword?.token)
      setPwSuccess('Password updated. Your other devices are signed out.')
      setOldPassword('')
      setNewPassword('')
      setConfirmPassword('')
      setTimeout(() => setPwSuccess(''), 5000)
    } catch (err) {
      setPwError(err.graphQLErrors?.[0]?.message || 'Failed to update password')
      setTimeout(() => setPwError(''), 5000)
    } finally {
      setSavingPw(false)
    }
  }

  const email = data?.getVendor?.email || ''
  const initials = (data?.getVendor?.name || 'V').charAt(0).toUpperCase()

  const pwFields = [
    { label: 'Current Password', value: oldPassword, set: setOldPassword, show: showOld, toggle: () => setShowOld(v => !v), placeholder: 'Current password' },
    { label: 'New Password', value: newPassword, set: setNewPassword, show: showNew, toggle: () => setShowNew(v => !v), placeholder: 'Min 6 characters' },
    { label: 'Confirm New Password', value: confirmPassword, set: setConfirmPassword, show: showConfirm, toggle: () => setShowConfirm(v => !v), placeholder: 'Repeat new password' }
  ]

  return (
    <>
      {/* Hero */}
      <Box sx={{
        background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 60%, #1e40af 100%)',
        px: { xs: 2, md: 4 }, pt: 3.5, pb: 5,
        position: 'relative', overflow: 'hidden',
        '&::before': {
          content: '""', position: 'absolute', top: -60, right: -60,
          width: 260, height: 260, borderRadius: '50%', background: 'rgba(255,255,255,0.04)'
        }
      }}>
        <Box sx={{ maxWidth: 900, mx: 'auto', display: 'flex', alignItems: 'center', gap: 2, position: 'relative', zIndex: 1 }}>
          <Avatar sx={{ width: 52, height: 52, bgcolor: '#f97316', fontSize: '22px', fontWeight: 'bold', flexShrink: 0 }}>
            {initials}
          </Avatar>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: '1.6rem', color: '#fff', lineHeight: 1.15, letterSpacing: '-0.02em' }}>
              My Profile
            </Typography>
            <Typography sx={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.55)', mt: 0.3 }}>
              {email}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Container maxWidth="sm" sx={{ mt: 3, pb: 4 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
        ) : (
          <>
            {/* Account Information */}
            <SectionCard
              title="Account Information"
              subtitle="Update your personal details"
              icon={<PersonIcon sx={{ color: '#fff', fontSize: 22 }} />}
            >
              {/* Email (read-only) */}
              <Box mb={2.5}>
                <FieldLabel>Email Address</FieldLabel>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1.2, borderRadius: '10px', bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                  <EmailIcon sx={{ fontSize: 16, color: '#94a3b8' }} />
                  <Typography sx={{ fontSize: '0.88rem', color: '#475569' }}>{email || '—'}</Typography>
                </Box>
                <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', mt: 0.5 }}>Email cannot be changed here</Typography>
              </Box>

              <form onSubmit={handleSave}>
                <Grid container spacing={2.5}>
                  <Grid item xs={12}>
                    <FieldLabel>Full Name</FieldLabel>
                    <TextField
                      fullWidth size="small" placeholder="Your name"
                      value={name} onChange={e => setName(e.target.value)}
                      sx={sx.input}
                      InputProps={{ startAdornment: <PersonIcon sx={{ fontSize: 16, color: '#94a3b8', mr: 0.75 }} /> }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FieldLabel>Phone Number</FieldLabel>
                    <TextField
                      fullWidth size="small" placeholder="+1 555 000 0000"
                      value={phone} onChange={e => setPhone(e.target.value)}
                      sx={sx.input}
                      InputProps={{ startAdornment: <PhoneIcon sx={{ fontSize: 16, color: '#94a3b8', mr: 0.75 }} /> }}
                    />
                  </Grid>
                </Grid>
                <Box sx={{ mt: 3 }}>
                  {success && <Alert severity="success" sx={{ mb: 2, borderRadius: '10px' }}>{success}</Alert>}
                  {error && <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }}>{error}</Alert>}
                  <Button type="submit" variant="contained" disabled={saving} sx={sx.btn}>
                    {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Save Changes'}
                  </Button>
                </Box>
              </form>
            </SectionCard>

            {/* Change Password */}
            <SectionCard
              title="Change Password"
              subtitle="Update your account login password"
              icon={<LockIcon sx={{ color: '#fff', fontSize: 22 }} />}
            >
              <form onSubmit={handleChangePassword}>
                <Grid container spacing={2.5}>
                  {pwFields.map(({ label, value, set, show, toggle, placeholder }) => (
                    <Grid item xs={12} key={label}>
                      <FieldLabel>{label}</FieldLabel>
                      <TextField
                        fullWidth size="small" placeholder={placeholder}
                        type={show ? 'text' : 'password'}
                        value={value} onChange={e => set(e.target.value)}
                        sx={sx.input}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton size="small" onClick={toggle} edge="end">
                                {show ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                              </IconButton>
                            </InputAdornment>
                          )
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
                <Box sx={{ mt: 3 }}>
                  {pwSuccess && <Alert severity="success" sx={{ mb: 2, borderRadius: '10px' }}>{pwSuccess}</Alert>}
                  {pwError && <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }}>{pwError}</Alert>}
                  <Button type="submit" variant="contained" disabled={savingPw} sx={sx.btn}>
                    {savingPw ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Update Password'}
                  </Button>
                </Box>
              </form>
            </SectionCard>
          </>
        )}
      </Container>
    </>
  )
}

export default VendorAccountProfile
